import { getSupabaseClient } from '../../lib/supabase'
import type { MarketplaceWorkspace } from './marketplace-types'
import { getOfferIntelligence } from './contractor-intelligence-service'

type RedirectResponse = { url?: string }
type RefundResponse = { paymentId: string; refundId: string; status: string }
type FunctionResult<T> = { data: T | null; error: Error | null }

export async function getMarketplaceWorkspace(organizationId: string, userId: string, mode:'customer'|'contractor'='customer'): Promise<MarketplaceWorkspace> {
  const supabase = getSupabaseClient()
  const [categories, contractor, memberships, customerRequests, matchedRequests, offers, assignments, connectedAccount, payments] = await Promise.all([
    supabase.from('service_categories').select('*').order('display_order'),
    supabase.from('contractor_profiles').select('*').eq('user_id', userId).maybeSingle(),
    supabase.from('contractor_category_memberships').select('category_id').eq('contractor_user_id', userId),
    supabase.from('service_requests').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }),
    mode==='contractor'?supabase.rpc('get_matched_service_requests'):Promise.resolve({data:[],error:null}),
    supabase.from('service_offers').select('*').order('created_at', { ascending: false }),
    supabase.from('service_job_assignments').select('*').order('created_at', { ascending: false }),
    supabase.from('marketplace_connected_accounts' as never).select('*').eq('contractor_user_id', userId).maybeSingle(),
    supabase.from('marketplace_payments' as never).select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }),
  ])
  const error = categories.error ?? contractor.error ?? memberships.error ?? customerRequests.error ?? matchedRequests.error ?? offers.error ?? assignments.error ?? connectedAccount.error ?? payments.error
  if (error) throw error
  const assignmentRows = assignments.data ?? []
  const assignedRequestIds = [...new Set(assignmentRows.map((item) => item.request_id))]
  const contractorIds = [...new Set([
    ...(offers.data ?? []).map((item) => item.contractor_user_id),
    ...assignmentRows.map((item) => item.contractor_user_id),
  ])]
  const [assignedRequests, activities, contractorProfiles] = await Promise.all([
    assignedRequestIds.length
      ? supabase.from('service_requests').select('*').in('id', assignedRequestIds)
      : Promise.resolve({ data: [], error: null }),
    assignedRequestIds.length
      ? supabase.from('service_job_activity').select('*').in('request_id', assignedRequestIds).order('created_at')
      : Promise.resolve({ data: [], error: null }),
    contractorIds.length
      ? supabase.from('contractor_profiles').select('user_id,business_name,trade_summary,base_city,base_region,insurance_verified,background_check_verified').in('user_id', contractorIds)
      : Promise.resolve({ data: [], error: null }),
  ])
  const detailError = assignedRequests.error ?? activities.error ?? contractorProfiles.error
  if (detailError) throw detailError
  const trustEntries = await Promise.all(contractorIds.map(async (contractorId) => {
    type TrustResult = PromiseLike<{ data:{ tier:string;badges:Record<string,string> }|null;error:{ message:string }|null }>
    const result = await (supabase.rpc('get_contractor_public_trust' as never,{ target_user_id:contractorId } as never) as unknown as TrustResult)
    if (result.error) throw new Error(result.error.message)
    return [contractorId,result.data ?? { tier:'unverified',badges:{} }] as const
  }))
  const intelligenceLists = await Promise.all((customerRequests.data ?? []).map(async request => [request.id,await getOfferIntelligence(request.id)] as const))
  const offerIntelligence = Object.fromEntries(intelligenceLists.flatMap(([requestId,items])=>items.map(item=>[`${requestId}:${item.contractorUserId}`,item])))
  return {
    categories: categories.data ?? [],
    contractor: contractor.data,
    contractorCategoryIds: (memberships.data ?? []).map((item) => item.category_id),
    customerRequests: customerRequests.data ?? [],
    matchedRequests: matchedRequests.data ?? [],
    assignedRequests: assignedRequests.data ?? [],
    offers: offers.data ?? [],
    assignments: assignmentRows,
    activities: activities.data ?? [],
    contractorProfiles: contractorProfiles.data ?? [],
    contractorTrust: Object.fromEntries(trustEntries),
    offerIntelligence,
    connectedAccount: connectedAccount.data,
    payments: payments.data ?? [],
  }
}

async function invokeUrlFunction(name: string, body: Record<string, string> = {}) {
  const result = await getSupabaseClient().functions.invoke<RedirectResponse>(name, { body }) as FunctionResult<RedirectResponse>
  if (result.error) throw result.error
  if (!result.data?.url) throw new Error('The secure redirect URL was not returned.')
  window.location.assign(result.data.url)
}

export async function createConnectOnboardingLink() {
  return invokeUrlFunction('create-connect-onboarding-link')
}

export async function createConnectDashboardLink() {
  return invokeUrlFunction('create-connect-dashboard-link')
}

export async function createMarketplaceCheckout(paymentId: string) {
  return invokeUrlFunction('create-marketplace-checkout', { paymentId })
}

export async function refundMarketplacePayment(paymentId: string) {
  const result = await getSupabaseClient().functions.invoke<RefundResponse>(
    'refund-marketplace-payment',
    { body: { paymentId } },
  ) as FunctionResult<RefundResponse>
  if (result.error) throw result.error
  return result.data
}

export async function registerContractor(userId: string, values: { businessName: string; tradeSummary: string; city: string; region: string; postalCode: string; radius: number; categoryIds: string[] }) {
  const supabase = getSupabaseClient()
  const { error } = await supabase.from('contractor_profiles').upsert({ user_id: userId, business_name: values.businessName.trim(), trade_summary: values.tradeSummary.trim(), base_city: values.city.trim(), base_region: values.region.trim(), base_postal_code: values.postalCode.trim(), service_radius_miles: values.radius }, { onConflict: 'user_id' })
  if (error) throw error
  const { error: deleteError } = await supabase.from('contractor_category_memberships').delete().eq('contractor_user_id', userId)
  if (deleteError) throw deleteError
  if (values.categoryIds.length) {
    const { error: categoryError } = await supabase.from('contractor_category_memberships').insert(values.categoryIds.map((category_id) => ({ contractor_user_id: userId, category_id })))
    if (categoryError) throw categoryError
  }
  type RpcResult = PromiseLike<{ error: { message: string } | null }>
  const { error: submitError } = await (supabase.rpc('submit_contractor_profile_for_review' as never) as unknown as RpcResult)
  if (submitError) throw new Error(submitError.message)
}

export async function createServiceRequest(organizationId: string, propertyId: string, userId: string, values: { categoryId: string; title: string; description: string; city: string; region: string; postalCode: string; budgetMin: number | null; budgetMax: number | null; desiredOn: string | null }) {
  const { error } = await getSupabaseClient().from('service_requests').insert({ organization_id: organizationId, property_id: propertyId, category_id: values.categoryId, title: values.title.trim(), description: values.description.trim(), city: values.city.trim(), region: values.region.trim(), postal_code: values.postalCode.trim(), budget_min_cents: values.budgetMin === null ? null : Math.round(values.budgetMin * 100), budget_max_cents: values.budgetMax === null ? null : Math.round(values.budgetMax * 100), desired_service_on: values.desiredOn || null, status: 'published', created_by: userId })
  if (error) throw error
}

export async function submitServiceOffer(requestId: string, userId: string, amount: number, days: number, availableStartDate: string, message: string) {
  const supabase = getSupabaseClient()
  const payload = {
    request_id: requestId,
    contractor_user_id: userId,
    amount_cents: Math.round(amount * 100),
    estimated_days: days,
    available_start_date: availableStartDate,
    message: message.trim(),
    status: 'submitted' as const,
  }
  const { data: existing, error: lookupError } = await supabase
    .from('service_offers')
    .select('id,status')
    .eq('request_id', requestId)
    .eq('contractor_user_id', userId)
    .maybeSingle()
  if (lookupError) throw lookupError

  if (!existing) {
    const { error } = await supabase.from('service_offers').insert(payload)
    if (error) throw error
    return
  }

  if (existing.status !== 'submitted' && existing.status !== 'withdrawn') {
    throw new Error('This offer can no longer be changed because it has already been reviewed.')
  }

  const offerUpdates = {
    amount_cents: payload.amount_cents,
    estimated_days: payload.estimated_days,
    available_start_date: payload.available_start_date,
    message: payload.message,
    status: payload.status,
  }
  const { data: updated, error } = await supabase
    .from('service_offers')
    .update(offerUpdates)
    .eq('id', existing.id)
    .select('id')
    .single()
  if (error) throw error
  if (!updated) throw new Error('Unable to update this offer.')
}

export async function acceptServiceOffer(offerId: string) {
  type RpcResult = PromiseLike<{ error: { message: string } | null }>
  const invoke = (name: string, args: Record<string, string>) =>
    getSupabaseClient().rpc(name as never, args as never) as unknown as RpcResult
  const { error } = await invoke('accept_service_offer', { target_offer_id: offerId })
  if (error) throw new Error(error.message)
}

export async function withdrawServiceOffer(offerId: string) {
  const { error } = await getSupabaseClient().from('service_offers').update({ status: 'withdrawn' }).eq('id', offerId)
  if (error) throw error
}

export async function transitionServiceJob(requestId: string, status: 'in_progress' | 'awaiting_approval' | 'completed' | 'cancelled', note?: string) {
  const { error } = await getSupabaseClient().rpc('transition_service_job', {
    target_request_id: requestId,
    target_status: status,
    transition_note: note?.trim() || null,
  })
  if (error) throw error
}
